import { Injectable } from '@angular/core';
import { Tag, Track } from '@app/core/models/Track';
import { RadioState } from '@app/core/states/radio.state';
import { FilterObject } from '@app/library/models/filters.model';
import { TagsService } from '@app/library/services/tags.service';
import { TrackService } from '@app/library/services/track.service';
import {
  TrackArchiveMultiBacSuccess,
  TrackArchiveSuccess,
  TrackCoverDeleteSuccess,
  TrackCoverLookupSuccess,
  TrackCoverMultipleSetSuccess,
  TrackCoverSingleSetSuccess,
  TrackEditSuccess,
  TracksTagsSaveSuccess,
  TrackTagsSaveSuccess,
} from '@app/library/states/tracks.actions';
import { Action, Selector, State, StateContext, Store } from '@ngxs/store';
import { compose, patch, removeItem, updateItem } from '@ngxs/store/operators';
import { CloseCurrentPanel } from '@radioking/shared/pannel-manager';
import { SugestionInput } from '@radioking/ui/input';
import { get } from 'lodash';
import { Observable } from 'rxjs';
import { catchError, filter, mergeMap, take } from 'rxjs/operators';

import {
  DeleteTagFailure,
  DeleteTagRequest,
  DeleteTagSuccess,
  EditTagFailure,
  EditTagRequest,
  EditTagSuccess,
  EndEditTag,
  RemoveTagFromTrackFailure,
  RemoveTagFromTrackRequest,
  RemoveTagFromTrackSuccess,
  RemoveTagsFromMultipleTrackFailure,
  RemoveTagsFromMultipleTrackRequest,
  RemoveTagsFromMultipleTrackSuccess,
  StartEditTag,
  TagsListFailure,
  TagsListRequest,
  TagsListSuccess,
  TagTracksFailure,
  TagTracksRequest,
  TagTracksSuccess,
} from './radio-tags.actions';

export class RadioTagsStateModel {
  tags: Tag[];
  isFetchingTags: boolean;
  isFetchingTracks: boolean;
  currentTagTracks: Track[];
  currentTag: Tag;
  editingTag: Tag;
}

@State<RadioTagsStateModel>({
  name: 'radioTags',
  defaults: {
    tags: undefined,
    isFetchingTags: false,
    isFetchingTracks: false,
    currentTagTracks: [],
    currentTag: null,
    editingTag: null,
  },
})
@Injectable()
export class RadioTagsState {
  constructor(
    private readonly store: Store,
    private readonly trackService: TrackService,
    private readonly tagsService: TagsService,
  ) {}
  @Selector()
  static tags(state: RadioTagsStateModel): Tag[] {
    return state.tags;
  }

  @Selector()
  static tagsFormatted(state: RadioTagsStateModel): SugestionInput[] {
    if (!state.tags) {
      return [];
    }

    return state.tags.map(item => ({ id: item.id, name: item.name, color: item.color }));
  }

  @Selector()
  static firstTagFormatted(state: RadioTagsStateModel): SugestionInput | void {
    if (!state.tags) {
      return;
    }

    return state.tags.map((item, i) => ({ id: i, name: item.name }))[0];
  }

  @Selector()
  static tagTracks(state: RadioTagsStateModel): Track[] {
    return state.currentTagTracks;
  }

  @Selector()
  static tagTracksTime(state: RadioTagsStateModel): number {
    return state.currentTagTracks.reduce(
      (pre: number, current: Track) => pre + current.timeSeconds,
      0,
    );
  }

  @Selector()
  static editingTag(state: RadioTagsStateModel): Tag {
    return state.editingTag;
  }

  @Selector()
  static tagsLoaded(state: RadioTagsStateModel): boolean {
    return !!state.tags;
  }

  @Selector()
  static trackById(state: RadioTagsStateModel) {
    return (id: number): Track => state.currentTagTracks.find(t => t.id === id);
  }

  @Selector()
  static isLoadingTags(state: RadioTagsStateModel) {
    return state.isFetchingTags;
  }

  @Selector()
  static isLoadingTagTracks(state: RadioTagsStateModel) {
    return state.isFetchingTags;
  }

  @Action(TagsListRequest)
  tagsRequest(ctx: StateContext<RadioTagsStateModel>, { force }: TagsListRequest) {
    if (ctx.getState().tags && !force) {
      return ctx.dispatch(new TagsListSuccess([], true));
    }
    ctx.patchState({ isFetchingTags: true });

    return this.store.select(RadioState.currentRadioId).pipe(
      filter(val => !!val),
      take(1),
      mergeMap(idRadio => this.tagsService.getRadioTags(idRadio)),
      mergeMap(arr => ctx.dispatch(new TagsListSuccess(arr, false))),
      catchError(err => ctx.dispatch(new TagsListFailure(err))),
    );
  }

  @Action(TagsListSuccess)
  tagsSuccess(
    ctx: StateContext<RadioTagsStateModel>,
    { isIgnoring, tags }: TagsListSuccess,
  ) {
    if (isIgnoring) {
      return;
    }
    ctx.patchState({ tags, isFetchingTags: false });
  }

  @Action(TrackTagsSaveSuccess)
  saveTagsSuccess(
    ctx: StateContext<RadioTagsStateModel>,
    { tags }: TrackTagsSaveSuccess,
  ) {
    ctx.patchState({
      tags: [
        ...ctx.getState().tags,
        ...tags.filter(
          tag => ctx.getState().tags.filter(t => t.name === tag.name).length === 0,
        ),
      ],
    });
  }

  @Action(TracksTagsSaveSuccess)
  saveMultipleTagsSuccess(
    ctx: StateContext<RadioTagsStateModel>,
    { tags, trackTags }: TracksTagsSaveSuccess,
  ) {
    const existingTagsNames = new Set(ctx.getState().tags.map(t => t.name));
    const tagsOfFirstTrack: Tag[] = get(trackTags, '0.tags', []);
    const newTags = tags.filter(t => !existingTagsNames.has(t.name));
    const newTagsWithResolvedId = newTags.map(tag => {
      const foundTag = tagsOfFirstTrack.find(t => t.name === tag.name) ?? tag;

      return { ...tag, id: foundTag.id };
    });
    ctx.patchState({
      tags: [...ctx.getState().tags, ...newTagsWithResolvedId],
    });
    const trackTagsMap = new Map(trackTags.map(tt => [tt.id, tt]));
    const tracks = ctx.getState().currentTagTracks.filter(tr => trackTagsMap.has(tr.id));
    const operators = tracks.map(track => {
      track.tags = trackTagsMap.get(track.id).tags;

      return patch<RadioTagsStateModel>({
        currentTagTracks: updateItem(t => t.id === track.id, track),
      });
    });
    ctx.setState(compose(...operators));
  }

  @Action(TagTracksRequest)
  tagTracks(ctx: StateContext<RadioTagsStateModel>, { tagObj }: TagTracksRequest) {
    const tag: Tag = {
      id: tagObj.id,
      name: tagObj.name,
      color: tagObj.color,
    };
    ctx.setState(
      patch<RadioTagsStateModel>({
        isFetchingTracks: true,
        currentTagTracks: [],
        currentTag: tag,
      }),
    );

    return this.getCurrentRadioId().pipe(
      mergeMap(id => {
        const filters: FilterObject = {
          tags: {
            operator: 'match_one',
            value: [tagObj],
          },
        };

        return this.trackService.getTagTracks(id, filters);
      }),
      mergeMap(data => ctx.dispatch(new TagTracksSuccess(data))),
      catchError(err => ctx.dispatch(new TagTracksFailure(err))),
    );
  }

  @Action(TagTracksSuccess)
  tagTracksSuccess(ctx: StateContext<RadioTagsStateModel>, { tracks }: TagTracksSuccess) {
    ctx.setState(
      patch<RadioTagsStateModel>({
        currentTagTracks: tracks,
        isFetchingTracks: false,
      }),
    );
  }

  @Action(TrackEditSuccess)
  trackEditSuccess(ctx: StateContext<RadioTagsStateModel>, { tracks }: TrackEditSuccess) {
    if (ctx.getState().currentTagTracks.length) {
      tracks.forEach(trackApi => {
        const track = this.trackService.convertToTrack(trackApi);
        ctx.setState(
          patch<RadioTagsStateModel>({
            currentTagTracks: updateItem(t => t.id === track.id, track),
          }),
        );
      });
    }
  }
  @Action([TrackCoverSingleSetSuccess, TrackCoverLookupSuccess, TrackCoverDeleteSuccess])
  setCustomCover(
    ctx: StateContext<RadioTagsStateModel>,
    {
      imgUrl,
      track,
    }: TrackCoverSingleSetSuccess | TrackCoverLookupSuccess | TrackCoverDeleteSuccess,
  ) {
    const mTrack: Track = { ...track, cover: imgUrl };
    ctx.setState(
      patch<RadioTagsStateModel>({
        currentTagTracks: updateItem(t => t.id === mTrack.id, mTrack),
      }),
    );
  }

  @Action(TrackCoverMultipleSetSuccess)
  setCustomCoverMultiple(
    ctx: StateContext<RadioTagsStateModel>,
    { tracks, tracksCover }: TrackCoverMultipleSetSuccess,
  ) {
    const operators = tracks.map((track, idx) => {
      const mTrack: Track = { ...track, cover: tracksCover[idx].cover_url };

      return patch<RadioTagsStateModel>({
        currentTagTracks: updateItem(t => t.id === mTrack.id, mTrack),
      });
    });
    ctx.setState(compose(...operators));
  }

  @Action(TrackTagsSaveSuccess)
  editTrackTags(
    ctx: StateContext<RadioTagsStateModel>,
    { id, tags }: TrackTagsSaveSuccess,
  ) {
    if (ctx.getState().currentTagTracks.length) {
      if (tags.map(t => t.id).indexOf(ctx.getState().currentTag.id) < 0) {
        ctx.setState(
          patch<RadioTagsStateModel>({
            currentTagTracks: ctx.getState().currentTagTracks.filter(t => t.id !== id),
          }),
        );
      } else {
        const track = ctx.getState().currentTagTracks.find(t => t.id === id);
        if (track) {
          track.tags = tags;
          const tracks = ctx
            .getState()
            .currentTagTracks.map(v => (v.id === id ? track : v));
          ctx.setState(
            patch<RadioTagsStateModel>({
              currentTagTracks: tracks,
            }),
          );
        }
      }
    }
  }

  @Action(TagTracksFailure)
  tagTracksFailure(ctx: StateContext<RadioTagsStateModel>) {
    ctx.setState(
      patch<RadioTagsStateModel>({
        isFetchingTracks: false,
      }),
    );
  }

  @Action(RemoveTagFromTrackRequest)
  deleteFromTag(
    ctx: StateContext<RadioTagsStateModel>,
    { track }: RemoveTagFromTrackRequest,
  ) {
    return this.getCurrentRadioId().pipe(
      mergeMap(id =>
        this.tagsService.editTags(
          id,
          track.id,
          track.tags.filter(tag => tag.id !== ctx.getState().currentTag.id),
        ),
      ),
      mergeMap(() => ctx.dispatch(new RemoveTagFromTrackSuccess(track.id))),
      catchError(err => ctx.dispatch(new RemoveTagFromTrackFailure(err))),
    );
  }

  @Action(RemoveTagFromTrackSuccess)
  deleteFromTagSuccess(
    ctx: StateContext<RadioTagsStateModel>,
    { idTrack }: RemoveTagFromTrackSuccess,
  ) {
    ctx.setState(
      patch<RadioTagsStateModel>({
        currentTagTracks: ctx
          .getState()
          .currentTagTracks.filter(track => track.id !== idTrack),
      }),
    );
  }

  @Action([TrackArchiveSuccess, TrackArchiveMultiBacSuccess])
  archiveTrackSuccess(
    ctx: StateContext<RadioTagsStateModel>,
    { tracksId }: TrackArchiveSuccess | TrackArchiveMultiBacSuccess,
  ) {
    ctx.setState(
      patch<RadioTagsStateModel>({
        currentTagTracks: ctx
          .getState()
          .currentTagTracks.filter(t => !tracksId.includes(t.id)),
      }),
    );
  }

  @Action(EditTagRequest)
  editTag(ctx: StateContext<RadioTagsStateModel>, { mTag }: EditTagRequest) {
    return this.getCurrentRadioId().pipe(
      mergeMap(id => this.tagsService.editTag(id, ctx.getState().editingTag.id, mTag)),
      mergeMap(tag => ctx.dispatch(new EditTagSuccess(tag))),
      catchError(err => ctx.dispatch(new EditTagFailure(err))),
    );
  }

  @Action(EditTagSuccess)
  editTagSuccess(ctx: StateContext<RadioTagsStateModel>, { mTag }: EditTagSuccess) {
    ctx.setState(
      patch<RadioTagsStateModel>({
        tags: ctx.getState().tags.map(tag => (tag.id === mTag.id ? mTag : tag)),
      }),
    );
    if (ctx.getState().currentTagTracks.length) {
      ctx.getState().currentTagTracks.forEach(track => {
        track.tags = track.tags.map(t => (t.id === mTag.id ? mTag : t));
        ctx.setState(
          patch<RadioTagsStateModel>({
            currentTagTracks: updateItem(t => t.id === track.id, track),
          }),
        );
      });
    }
    ctx.dispatch(new CloseCurrentPanel(true));
  }

  @Action(DeleteTagRequest)
  deleteTag(ctx: StateContext<RadioTagsStateModel>, { mTag }: DeleteTagRequest) {
    return this.getCurrentRadioId().pipe(
      mergeMap(id => this.tagsService.deleteTag(id, mTag.id)),
      mergeMap(() => ctx.dispatch(new DeleteTagSuccess(mTag))),
      catchError(err => ctx.dispatch(new DeleteTagFailure(err))),
    );
  }

  @Action(DeleteTagSuccess)
  deleteTagSuccess(ctx: StateContext<RadioTagsStateModel>, { mTag }: DeleteTagSuccess) {
    ctx.setState(
      patch({
        tags: removeItem((t: Tag) => t.id === mTag.id),
      }),
    );
  }

  @Action(StartEditTag)
  startEditTag(ctx: StateContext<RadioTagsStateModel>, { mTag }: StartEditTag) {
    ctx.setState(
      patch<RadioTagsStateModel>({
        editingTag: mTag,
      }),
    );
  }

  @Action(EndEditTag)
  endEditTag(ctx: StateContext<RadioTagsStateModel>) {
    ctx.dispatch(new CloseCurrentPanel(true));
    ctx.setState(
      patch<RadioTagsStateModel>({
        editingTag: null,
      }),
    );
  }

  @Action(RemoveTagsFromMultipleTrackRequest)
  removeTagsFromTracks(
    ctx: StateContext<RadioTagsStateModel>,
    { idTracks, idTags }: RemoveTagsFromMultipleTrackRequest,
  ) {
    return this.getCurrentRadioId().pipe(
      mergeMap(id => this.tagsService.removeTags(id, idTracks, idTags)),
      mergeMap(() =>
        ctx.dispatch(new RemoveTagsFromMultipleTrackSuccess(idTracks, idTags)),
      ),
      catchError(err => ctx.dispatch(new RemoveTagsFromMultipleTrackFailure(err))),
    );
  }

  @Action(RemoveTagsFromMultipleTrackSuccess)
  removeTagsFromTracksSuccess(
    ctx: StateContext<RadioTagsStateModel>,
    { idTracks, idTags }: RemoveTagsFromMultipleTrackSuccess,
  ) {
    if (ctx.getState().currentTagTracks.length) {
      const idTracksSet = new Set(idTracks);
      const idTagsSet = new Set(idTags);

      if (idTagsSet.has(ctx.getState().currentTag.id)) {
        ctx.setState(
          patch<RadioTagsStateModel>({
            currentTagTracks: ctx
              .getState()
              .currentTagTracks.filter(track => !idTracksSet.has(track.id)),
          }),
        );
      } else {
        const tracks = ctx.getState().currentTagTracks.filter(t => idTracksSet.has(t.id));
        const operators = tracks.map(track => {
          track.tags = track.tags.filter(tag => !idTagsSet.has(tag.id));

          return patch<RadioTagsStateModel>({
            currentTagTracks: updateItem(t => t.id === track.id, track),
          });
        });

        ctx.setState(compose(...operators));
      }
    }
  }

  getCurrentRadioId(): Observable<number> {
    return this.store.select(RadioState.currentRadioId).pipe(
      filter(data => !!data),
      take(1),
    );
  }
}
